import { useCallback, useEffect, useMemo, useState } from 'react';
import { Bell, BellOff, CheckCircle2, Clock3, LogOut, RadioTower, Settings, ShieldAlert, WifiOff } from 'lucide-react';
import { ActiveCallScreen } from '../components/ActiveCallScreen';
import { CallButtons } from '../components/CallButtons';
import { BrandHeader } from '../components/BrandHeader';
import { ConfirmOverlay } from '../components/ConfirmOverlay';
import { IncomingCallOverlay } from '../components/IncomingCallOverlay';
import { IncomingAlert } from '../components/IncomingAlert';
import { InstallPrompt } from '../components/InstallPrompt';
import { SosButton } from '../components/SosButton';
import { StatusCard } from '../components/StatusCard';
import {
  acknowledgeAlert,
  getAlerts,
  getStatus,
  sendSos,
  type EmergencyAlert,
  type StatusResponse,
} from '../services/api';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useCall } from '../hooks/useCall';
import { getNotificationTone } from '../lib/notifications';
import { getOtherUser, type Username } from '../utils/constants';
import { toFriendlyError } from '../utils/errors';

type HomePageProps = {
  username: Username;
  token: string;
  onLogout: () => Promise<void>;
  onOpenSettings: () => void;
};

export function HomePage({ username, token, onLogout, onOpenSettings }: HomePageProps) {
  const push = usePushNotifications(token);
  const call = useCall(token, username);
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [result, setResult] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [incomingAlert, setIncomingAlert] = useState<EmergencyAlert | null>(null);
  const [outgoingAlert, setOutgoingAlert] = useState<EmergencyAlert | null>(null);
  const receiver = getOtherUser(username);
  const tone = useMemo(() => getNotificationTone(push.permission, push.isSupported), [push.isSupported, push.permission]);
  const canSend = Date.now() > cooldownUntil && !isSending;

  const refreshStatus = useCallback(async () => {
    const [nextStatus, nextAlerts] = await Promise.all([getStatus(token), getAlerts(token)]);
    setStatus(nextStatus);
    setIncomingAlert(nextAlerts.incoming);
    setOutgoingAlert(nextAlerts.outgoing);
  }, [token]);

  useEffect(() => {
    refreshStatus().catch((error: unknown) => setResult(toFriendlyError(error)));
    const interval = window.setInterval(() => {
      refreshStatus().catch(() => undefined);
    }, 5000);

    return () => window.clearInterval(interval);
  }, [refreshStatus]);

  async function handleRegister() {
    const ok = await push.register();
    if (ok) {
      await refreshStatus().catch(() => undefined);
    }
  }

  async function handleSend() {
    setIsSending(true);
    setResult('');

    try {
      const response = await sendSos(token);
      setOutgoingAlert(response.alert);

      if (response.delivery) {
        const attemptCopy = response.delivery.attempts === 1 ? '1 attempt' : `${response.delivery.attempts} attempts`;
        const deviceCopy =
          response.delivery.acceptedDevices && response.delivery.totalDevices
            ? `${response.delivery.acceptedDevices}/${response.delivery.totalDevices} devices`
            : response.alert.receiver;
        setResult(`Emergency alert active. Push accepted for ${deviceCopy} after ${attemptCopy}.`);
      } else {
        setResult(response.warning || 'Emergency alert active in app. Push notification was not confirmed.');
      }

      setCooldownUntil(Date.now() + 8000);
      setIsConfirming(false);
      await refreshStatus().catch(() => undefined);
    } catch (error: unknown) {
      setResult(toFriendlyError(error));
    } finally {
      setIsSending(false);
    }
  }

  async function handleAcknowledgeAlert() {
    if (!incomingAlert) {
      return;
    }

    setIsAcknowledging(true);

    try {
      await acknowledgeAlert(token, incomingAlert.id);
      setIncomingAlert(null);
      setResult(`Acknowledged SOS from ${incomingAlert.sender}.`);
      await refreshStatus().catch(() => undefined);
    } catch (error: unknown) {
      setResult(toFriendlyError(error));
    } finally {
      setIsAcknowledging(false);
    }
  }

  return (
    <main className="app-shell">
      <section className="hero-panel" aria-labelledby="home-title">
        <BrandHeader
          eyebrow={`Signed in as ${username}`}
          action={
            <div className="top-actions">
              <InstallPrompt />
              <button className="icon-action" type="button" onClick={onOpenSettings} aria-label="Open settings">
                <Settings size={17} aria-hidden="true" />
              </button>
              <button className="icon-action" type="button" onClick={onLogout} aria-label="Log out">
                <LogOut size={17} aria-hidden="true" />
              </button>
            </div>
          }
        />

        <div className="greeting-row">
          <div>
            <p className="panel-kicker">Emergency relay</p>
            <h2 id="home-title">Ready for {receiver}</h2>
            <p>Alerts use the strongest notification behavior allowed by your phone and browser.</p>
          </div>
          <span className={`live-badge ${status?.receiverHasNotifications ? 'success' : 'warning'}`}>
            <span aria-hidden="true" />
            {status?.receiverHasNotifications ? 'Receiver ready' : 'Receiver pending'}
          </span>
        </div>

        <CallButtons disabled={call.phase !== 'idle'} onStart={call.start} />

        <SosButton disabled={!canSend} isSending={isSending} onPress={() => setIsConfirming(true)} />

        <div className="permission-panel">
          <div className="permission-copy">
            <span className={`permission-icon ${tone.intent}`}>
              {tone.intent === 'success' ? (
                <CheckCircle2 size={22} aria-hidden="true" />
              ) : tone.intent === 'danger' ? (
                <BellOff size={22} aria-hidden="true" />
              ) : (
                <Bell size={22} aria-hidden="true" />
              )}
            </span>
            <div>
              <p className="panel-kicker">Notification permission</p>
              <h2>{tone.title}</h2>
              <p>{push.message || tone.description}</p>
            </div>
          </div>
          <button className="notify-button" type="button" onClick={handleRegister} disabled={push.isRegistering}>
            <Bell size={18} aria-hidden="true" />
            {push.isRegistering ? 'Registering...' : 'Enable notifications'}
          </button>
        </div>

        {result ? <p className="result-banner">{result}</p> : null}

        {outgoingAlert ? (
          <div className={`ack-card ${outgoingAlert.status}`}>
            <Clock3 size={18} aria-hidden="true" />
            <div>
              <p className="panel-kicker">Last SOS</p>
              <h2>{outgoingAlert.status === 'acknowledged' ? 'Receiver acknowledged' : 'Waiting for acknowledgement'}</h2>
              <p>
                Push: {outgoingAlert.pushStatus}
                {outgoingAlert.pushError ? ` - ${outgoingAlert.pushError}` : ''}
              </p>
            </div>
          </div>
        ) : null}
      </section>

      <section className="status-grid" aria-label="System status">
        <StatusCard
          icon={RadioTower}
          label="Receiver"
          value={status?.receiverHasNotifications ? `${status.receiverDeviceCount} devices` : 'Not registered'}
          tone={status?.receiverHasNotifications ? 'success' : 'warning'}
        />
        <StatusCard
          icon={ShieldAlert}
          label="Your device"
          value={status?.currentUserHasNotifications || push.hasSubscription ? `${status?.currentUserDeviceCount ?? 1} devices` : 'Pending'}
          tone={status?.currentUserHasNotifications || push.hasSubscription ? 'success' : 'warning'}
        />
        <StatusCard icon={navigator.onLine ? CheckCircle2 : WifiOff} label="Network" value={navigator.onLine ? 'Online' : 'Offline'} />
      </section>

      <aside className="safety-note" aria-label="Emergency reminder">
        <ShieldAlert size={18} aria-hidden="true" />
        <p>Tap only in emergency. For immediate danger, contact local emergency services directly.</p>
      </aside>

      <ConfirmOverlay
        isOpen={isConfirming}
        isSending={isSending}
        receiver={receiver}
        onCancel={() => setIsConfirming(false)}
        onConfirm={handleSend}
      />

      <IncomingAlert alert={incomingAlert} isAcknowledging={isAcknowledging} onAcknowledge={handleAcknowledgeAlert} />

      <IncomingCallOverlay incoming={call.incoming} isBusy={call.phase === 'connecting'} onAccept={call.accept} onReject={call.reject} />

      <ActiveCallScreen
        isOpen={call.phase === 'outgoing' || call.phase === 'connecting' || call.phase === 'active'}
        mode={call.mode}
        peerName={call.peerName}
        status={call.status}
        error={call.error}
        muted={call.muted}
        cameraOff={call.cameraOff}
        speakerOff={call.speakerOff}
        durationSeconds={call.durationSeconds}
        quality={call.quality}
        reconnecting={call.reconnecting}
        verificationCode={call.verificationCode}
        localStream={call.localStream}
        remoteStream={call.remoteStream}
        onToggleMute={call.toggleMute}
        onToggleCamera={call.toggleCamera}
        onToggleSpeaker={call.toggleSpeaker}
        onSwitchCamera={call.switchCamera}
        onEnd={call.end}
      />
    </main>
  );
}
